import React, { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import mvPrimaryLogo from '../../assets/img/mv-primary.png'
import { HostData, HostDataUtil } from '../Customer/Data'
import { InfoIcon } from '../Icons/Icons'
import { STAR_BIG, STAR_LOW, STAR_TOP } from './hubBrand'
import { ReactComponent as StampRing } from '../../assets/img/stamp-ring.svg'
import { Timestamps } from '../../service/Timestamps'
import { Chatbot } from './Chatbot'
import { BriefGuide, hasSeenGuide } from './BriefGuide'
import { BriefTourDiagram } from './BriefTourDiagram'
import { assistantNameFor, useDemoOptions } from './DemoOptionsPanel' /* DEMO ONLY */
import { usePinnedNoteLayout } from './usePinnedNoteLayout'
import { usePrintInLightTheme } from './usePrintInLightTheme'
import styles from './BriefShell.module.css'

/* what each brief is for, in a line: the tip on the i beside its title */
export const BRIEF_PURPOSES: Record<string, string> = {
  'health-check': 'Fleet makeup, print volume, feature use and renewal risk in one view.',
  'fleet-makeup': 'What devices and print queues make up the fleet, and where to refresh or expand.',
  security: 'Security posture of the PaperCut deployment, starting with version and platform.',
  /* DEMO ONLY: the mock Security brief (SecurityDemoBrief) */
  'security-demo':
    'Security posture of the PaperCut deployment: version and platform, access control and document protection.',
}

export type ViewMode = 'vertical' | 'horizontal'

export interface BriefContentContext {
  activeSideNotes: boolean
  isVertical: boolean
  /** the metric pods are showing above the brief (both layouts, except full screen) */
  podsVisible: boolean
  onNoteLayoutChange: () => void
}

interface Props {
  teamID: string
  host: HostData
  briefType: string
  title: string
  /** when the brief was generated, shown in the report details under the title */
  generatedAt?: string
  industry?: string
  /** Overview content remains the responsibility of the individual brief. */
  renderOverview: (context: BriefContentContext) => ReactNode
  children: (context: BriefContentContext) => ReactNode
  verticalPods?: ReactNode
  layoutContent?: unknown
  segmentTestId?: string
  suggestionChips?: string[]
}

/* the AI's stars as a faint rubber stamp on the brief's stub (the headline numbers): these are AI-made. A
   rough, inky ring round the stars, rotated, and larger than the stub so it's cut off at its edges. */
const AiStamp: FunctionComponent = () => (
  <span className={styles.aiStampClip}>
    <span className={styles.aiStamp} title='Made with AI'>
      <svg viewBox='0 0 64 64' aria-hidden='true'>
        <StampRing x='0' y='0' width='64' height='64' />
        <g transform='translate(14.9 14.8) scale(0.74)' fill='currentColor'>
          <path d={STAR_BIG} />
          <path d={STAR_TOP} />
          <path d={STAR_LOW} />
        </g>
      </svg>
    </span>
  </span>
)

export const BriefShell: FunctionComponent<Props> = ({
  teamID,
  host,
  briefType,
  title,
  generatedAt,
  industry,
  renderOverview,
  children,
  verticalPods,
  layoutContent,
  segmentTestId,
  suggestionChips,
}) => {
  const [searchParams] = useSearchParams()
  // side-by-side by default; ?view=vertical opens stacked
  const [viewMode, setViewMode] = useState<ViewMode>(
    searchParams.get('view') === 'vertical' ? 'vertical' : 'horizontal'
  )
  const [showNotes, setShowNotes] = useState(true)
  // the walkthrough is written for the Health check; it opens on a first visit
  const hasGuide = briefType === 'health-check'
  const { options } = useDemoOptions() /* DEMO ONLY: how the tour shows */
  const diagramTour = options.tourStyle === 'diagram'
  const missionTitle = assistantNameFor(options.assistantName).tourTitle
  const [showGuide, setShowGuide] = useState(() => hasGuide && !hasSeenGuide())
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isVerticalChatExpanded, setIsVerticalChatExpanded] = useState(false)
  const [noteLayoutVersion, setNoteLayoutVersion] = useState(0)
  const briefContentRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const isVertical = viewMode === 'vertical' && !isFullscreen
  const isHorizontal = viewMode === 'horizontal' && !isFullscreen
  const activeSideNotes = !isFullscreen && showNotes
  const onNoteLayoutChange = () => setNoteLayoutVersion(version => version + 1)
  const podsVisible = !isFullscreen && Boolean(verticalPods)
  const context: BriefContentContext = { activeSideNotes, isVertical, podsVisible, onNoteLayoutChange }

  usePinnedNoteLayout(briefContentRef, activeSideNotes, layoutContent, viewMode, noteLayoutVersion)
  usePrintInLightTheme()

  useEffect(() => {
    const wrapEl = document.querySelector('.wrap')
    wrapEl?.classList.toggle('fullscreen-mode', isFullscreen)
    return () => wrapEl?.classList.remove('fullscreen-mode')
  }, [isFullscreen])

  useEffect(() => {
    const timer = setTimeout(() => window.dispatchEvent(new Event('resize')), 350)
    return () => clearTimeout(timer)
  }, [viewMode, showNotes, isFullscreen])

  const handleToggleFullscreen = () => setIsFullscreen(previous => !previous)
  // after the first switch, the stacked Mission Control appears in place rather than replaying its entrance
  const [hasSwitchedView, setHasSwitchedView] = useState(false)

  const handleViewModeChange = (mode: ViewMode) => {
    const apply = () => {
      setHasSwitchedView(true)
      setViewMode(mode)
    }
    const doc = document as Document & {
      startViewTransition?: (update: () => void) => { finished: Promise<void> }
    }
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (mode === viewMode || !doc.startViewTransition || reduceMotion) {
      apply()
      return
    }
    // Mission Control morphs between the floating bar (stacked) and the side panel (side-by-side):
    // both are named for the transition (see BriefShell.module.css), so it glides in size and position
    const root = document.documentElement
    root.dataset.briefMorph = mode === 'horizontal' ? 'to-sidebar' : 'to-stacked'
    const cleanUp = () => {
      delete root.dataset.briefMorph
    }
    doc.startViewTransition(apply).finished.then(cleanUp, cleanUp)
  }

  // the diagram tour is up: the areas it points to each give a little show of themselves (BriefShell.module.css)
  const diagramShown = hasGuide && showGuide && diagramTour && !isFullscreen
  // the AI borders' spin plays each time the diagram tour opens, and only then: its class stays once it has opened
  // (taking it away would set the assistant's own load animation going again), and opening it again replays it
  const rootRef = useRef<HTMLDivElement>(null)
  const [tourPlayed, setTourPlayed] = useState(false)
  useEffect(() => {
    if (!diagramShown) return
    if (!tourPlayed) {
      setTourPlayed(true)
      return
    }
    const root = rootRef.current
    if (!root || typeof root.getAnimations !== 'function') return
    root
      .getAnimations({ subtree: true })
      .filter(a => /tour(Spin|Border|Arrow)/.test((a as CSSAnimation).animationName ?? ''))
      .forEach(a => {
        a.cancel()
        a.play()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only as the tour opens
  }, [diagramShown])

  return (
    <div
      ref={rootRef}
      data-testid={`${briefType}-brief`}
      className={
        [diagramShown && styles.tourDiagramOn, tourPlayed && styles.tourPlayed].filter(Boolean).join(' ') || undefined
      }
    >
      {/* Header Divider */}
      <div className={styles.headerDivider}>
        {/* Left Part: Back button & Breadcrumb */}
        <div className={styles.headerLeft}>
          <button onClick={() => navigate(`/intelligence-hub/${host.id}/briefs`)} className={styles.backButton}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
              className={styles.iconSubdued}
            >
              <polyline points='15 18 9 12 15 6'></polyline>
            </svg>
          </button>

          <div className={styles.breadcrumb}>
            <Link to={`/intelligence-hub/${host.id}`} className={styles.breadcrumbLink}>
              {host.name}
            </Link>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className={styles.iconSeparator}
            >
              <polyline points='9 18 15 12 9 6'></polyline>
            </svg>
            <span>{title}</span>
          </div>
        </div>

        {/* Right Part: View Toggle, Notes & Download */}
        <div className={styles.headerRight} data-guide='view'>
          {/* 2-Button Segmented View Control */}
          <div className={styles.view}>
            {/* 1. Side-by-side */}
            <a
              href='#side-by-side'
              className={`has-tip${isHorizontal ? ` ${styles.active}` : ''}`}
              onClick={e => {
                e.preventDefault()
                if (isHorizontal && showNotes) {
                  setShowNotes(false)
                } else {
                  handleViewModeChange('horizontal')
                }
              }}
              aria-label={showNotes ? 'Switch to side-by-side without notes view' : 'Switch to side-by-side view'}
            >
              <span className='tip center'>Side-by-side view</span>
              <svg fill='none' viewBox='-1 -1 24 24'>
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='m2.75 20.622341666666667 16.5 0c0.7593666666666666 0 1.375 -0.6156333333333333 1.375 -1.375l0 -16.500027499999998c0 -0.7593941666666666 -0.6156333333333333 -1.375 -1.375 -1.375l-16.5 0c-0.759385 0 -1.375 0.6156058333333333 -1.375 1.375l0 16.500027499999998c0 0.7593666666666666 0.6156058333333333 1.375 1.375 1.375Z'
                  strokeWidth='2'
                ></path>
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='m14.208333333333332 20.622341666666667 0 -19.250027499999998'
                  strokeWidth='2'
                ></path>
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='m7.791666666666666 20.622341666666667 0 -19.250027499999998'
                  strokeWidth='2'
                ></path>
              </svg>
            </a>

            {/* 2. Stacked */}
            <a
              href='#stacked'
              className={`has-tip${isVertical ? ` ${styles.active}` : ''}`}
              onClick={e => {
                e.preventDefault()
                handleViewModeChange('vertical')
              }}
              aria-label={showNotes ? 'Switch to stacked view' : 'Switch to stacked without notes view'}
            >
              <span className='tip center'>Stacked view</span>
              <svg fill='none' viewBox='-1 -1 24 24'>
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='m20.625 19.25 0 -16.5c0 -0.7593941666666666 -0.6156333333333333 -1.375 -1.375 -1.375l-16.5 0c-0.7593941666666666 0 -1.375 0.6156058333333333 -1.375 1.375l0 16.5c0 0.7593666666666666 0.6156058333333333 1.375 1.375 1.375l16.5 0c0.7593666666666666 0 1.375 -0.6156333333333333 1.375 -1.375Z'
                  strokeWidth='2'
                ></path>
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='m20.625 7.791666666666666 -19.25 0'
                  strokeWidth='2'
                ></path>
                <path
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='m20.625 14.208333333333332 -19.25 0'
                  strokeWidth='2'
                ></path>
              </svg>
            </a>
          </div>

          {/* Notes on/off: a checkbox inside a button-shaped label, so its state is always visible */}
          <label className={`btn secondary ${styles.toolbarButton} ${styles.notesToggle}`}>
            <input
              type='checkbox'
              className={styles.notesCheckbox}
              checked={showNotes}
              onChange={e => setShowNotes(e.target.checked)}
            />
            Notes
          </label>

          {hasGuide && (
            <button
              type='button'
              className={`btn secondary ${styles.toolbarButton} ${styles.tourButton}${showGuide ? ' active' : ''}`}
              aria-pressed={showGuide}
              onClick={() => setShowGuide(prev => !prev)}
            >
              Tour
            </button>
          )}

          {/* Print and Save as PDF both go through the browser's print dialog */}
          <button type='button' className={`btn secondary ${styles.toolbarButton}`} onClick={() => window.print()}>
            <svg
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden='true'
            >
              <path d='M8 9H6.5A1.5 1.5 0 0 0 5 10.5v9A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 17.5 9H16' />
              <path d='M12 3v11' />
              <path d='M8.5 6.5 12 3l3.5 3.5' />
            </svg>
            Print / PDF
          </button>
        </div>
      </div>

      {/* the tour as a diagram: a slim band above the layout, pointing to each area */}
      {diagramShown && <BriefTourDiagram layoutKey={`${viewMode}-${showNotes}`} missionTitle={missionTitle} />}

      {/* Main Layout */}
      <div className={styles.briefLayout}>
        {/* Horizontal Mode: Left Chatbot Panel */}
        {isHorizontal && (
          <div className={styles.chatbotWrapper} data-guide='mission'>
            <Chatbot
              teamID={teamID}
              customerID={host.id}
              briefType={briefType}
              className={styles.chatbotInner}
              hideViewModeOptions={false}
            />
          </div>
        )}

        <div
          className={`${styles.briefSegments}${isVertical ? ` ${styles.briefSegmentsVertical}` : ''}${
            activeSideNotes ? ` ${styles.briefSegmentsWithNotes}` : ''
          }`}
        >
          {/* metric pods above the brief, in both stacked and side-by-side layouts (DEMO ONLY: or as its stub) */}
          {podsVisible && !options.briefTicketStub && (
            <div className={styles.verticalPodsContainer} data-guide='metrics'>
              {verticalPods}
            </div>
          )}
          <div
            className={`${styles.briefSegment}${!activeSideNotes ? ` ${styles.notesHidden}` : ''}`}
            data-testid={segmentTestId}
          >
            <div
              ref={briefContentRef}
              className={`${styles.briefContent}${activeSideNotes ? ` ${styles.briefContentWithNotes}` : ''}`}
            >
              <div className='paperOverlay'></div>
              {/* the headline numbers: a stub along the top of the brief, torn off along a dashed line */}
              {podsVisible && options.briefTicketStub && (
                <div className={styles.stubSlot} data-guide='metrics'>
                  <div className={styles.briefStub}>
                    {verticalPods}
                    <AiStamp />
                  </div>
                </div>
              )}
              <div className={styles.sectionWithNote}>
                <header className={styles.briefHeader}>
                  <div className={styles.briefTitleBlock}>
                    <h2 className={styles.briefTitle}>{title}</h2>
                    {/* what this brief is for, in the standard tip */}
                    {BRIEF_PURPOSES[briefType] && (
                      <span className={`has-tip ${styles.briefPurpose}`}>
                        <span className={`tip center ${styles.briefPurposeTip}`}>{BRIEF_PURPOSES[briefType]}</span>
                        <i className='icon twelve' aria-hidden='true'>
                          <InfoIcon />
                        </i>
                      </span>
                    )}
                  </div>
                  <img src={mvPrimaryLogo} alt='PaperCut Multiverse' className={styles.printLogo} />
                  <button
                    type='button'
                    onClick={handleToggleFullscreen}
                    className={`${styles.fullscreenButton} has-tip`}
                    aria-label={isFullscreen ? 'Exit full screen' : 'Full screen brief'}
                  >
                    <span className='tip center'>{isFullscreen ? 'Exit full screen' : 'Full screen brief'}</span>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      {isFullscreen ? (
                        <>
                          <polyline points='4 14 10 14 10 20' />
                          <polyline points='20 10 14 10 14 4' />
                          <line x1='14' y1='10' x2='21' y2='3' />
                          <line x1='3' y1='21' x2='10' y2='14' />
                        </>
                      ) : (
                        <>
                          <polyline points='15 3 21 3 21 9' />
                          <polyline points='9 21 3 21 3 15' />
                          <line x1='21' y1='3' x2='14' y2='10' />
                          <line x1='3' y1='21' x2='10' y2='14' />
                        </>
                      )}
                    </svg>
                  </button>
                </header>

                {/* report details: full width, under the title row and its buttons */}
                <dl className={styles.briefMeta}>
                  <div>
                    <dt>Customer</dt>
                    <dd>{host.name}</dd>
                  </div>
                  <div>
                    <dt>Industry</dt>
                    <dd>{industry || '—'}</dd>
                  </div>
                  <div>
                    <dt>Product</dt>
                    <dd>{HostDataUtil.getProductText(host.product)}</dd>
                  </div>
                  <div>
                    <dt>Generated</dt>
                    <dd>{generatedAt ? Timestamps.format(new Date(generatedAt)) : '—'}</dd>
                  </div>
                </dl>

                {renderOverview(context)}
              </div>
              {children(context)}
            </div>
          </div>
        </div>
      </div>
      {/* Vertical Mode: Floating Chatbot at Bottom Center with Staggered Peek Chips */}
      {isVertical && (
        <>
          {!isVerticalChatExpanded && suggestionChips && suggestionChips.length > 0 && (
            <div className={styles.suggestionChipsPeekContainer}>
              {suggestionChips[0] && (
                <button
                  className={`${styles.suggestionChip} ${styles.chip1}`}
                  onClick={() => setIsVerticalChatExpanded(true)}
                >
                  {suggestionChips[0]}
                </button>
              )}
              {suggestionChips[1] && (
                <button
                  className={`${styles.suggestionChip} ${styles.chip2}`}
                  onClick={() => setIsVerticalChatExpanded(true)}
                >
                  {suggestionChips[1]}
                </button>
              )}
              {suggestionChips[2] && (
                <button
                  className={`${styles.suggestionChip} ${styles.chip3}`}
                  onClick={() => setIsVerticalChatExpanded(true)}
                >
                  {suggestionChips[2]}
                </button>
              )}
            </div>
          )}

          <div
            data-guide='mission'
            className={`${styles.chatbotVerticalWrapper}${isVerticalChatExpanded ? ` ${styles.verticalExpanded}` : ''}${
              hasSwitchedView ? ` ${styles.arrived}` : ''
            }`}
          >
            <Chatbot
              teamID={teamID}
              customerID={host.id}
              briefType={briefType}
              className={styles.chatbotVerticalInner}
              hideViewModeOptions={true}
              onExpand={() => setIsVerticalChatExpanded(true)}
              onCollapse={() => setIsVerticalChatExpanded(false)}
            />
          </div>
        </>
      )}

      {/* Optional walkthrough; toggled from the Tour button */}
      {hasGuide && showGuide && !diagramTour && !isFullscreen && (
        <BriefGuide missionTitle={missionTitle} onClose={() => setShowGuide(false)} />
      )}
    </div>
  )
}
